/**
 * MODIFIED: 2025-01-29 - refactor/task-1.5-dashboard-performance
 *
 * ARCHITECTURE: Svelte 5 Runes State Management
 * PURPOSE: Unified notification system combining persistent notifications (database-backed)
 * with transient toast messages for user alerts and system messages
 *
 * NEW PATTERNS IMPLEMENTED:
 * - Runes Pattern: $state for reactive notification and toast collections
 * - Derived State: $derived for unread counts and filtered views
 * - Dual System: Persistent notifications + ephemeral toasts
 * - Auto-Expiry: Automatic toast removal based on duration
 *
 * KEY FEATURES:
 * - Persistent notifications with read/unread status
 * - Toast messages with auto-dismiss (success/error/warning/info)
 * - Unread count tracking with real-time updates
 * - Notification preferences per event type
 * - Mark as read/dismiss functionality
 * - EventBus integration for system-wide notifications
 *
 * RELATED FILES:
 * - src/lib/services/notifications/notification.actions.svelte.ts - Notification API
 * - src/lib/types/notifications.ts - Notification type definitions
 * - src/lib/components/common/Toast.svelte - Toast UI component
 */

/**
 * Notification State Management - Svelte 5 runes
 * Manages notification data, unread counts, and UI state
 */

import type {
	Notification,
	NotificationPreference,
	NotificationEventType,
	NotificationEvent
} from '$lib/types/notifications';
import { eventBus } from '$lib/core/services/EventBusService';
import {
	fetchNotifications,
	fetchUnreadCount,
	fetchPreferences,
	markNotificationAsRead as markAsReadApi,
	dismissNotificationById as dismissApi,
	markAllNotificationsAsRead as markAllAsReadApi
} from '$lib/services/notifications/notification.actions.svelte';
import { createLogger } from '$lib/utils/logger';

const logger = createLogger('state.notifications');

// Toast message type for temporary notifications (consolidated from toastStore.ts)
export type ToastMessage = {
	id: string;
	type: 'success' | 'error' | 'warning' | 'info';
	message: string;
	duration?: number;
	color?: string;
	iconClass?: string;
	customIcon?: boolean;
	icon?: any; // Component type
	timestamp: Date;
};

interface NotificationState {
	notifications: Notification[];
	toasts: ToastMessage[]; // Added toast functionality
	unreadCount: number;
	preferences: NotificationPreference[];
	isLoading: boolean;
	error: string | null;
	lastUpdated: Date | null;
	initialized: boolean;
}

// Internal state using $state
const state = $state<NotificationState>({
	notifications: [],
	toasts: [], // Added toast array
	unreadCount: 0,
	preferences: [],
	isLoading: false,
	error: null,
	lastUpdated: null,
	initialized: false
});

// Derived values using $derived
const hasUnread = $derived(state.unreadCount > 0);
const recentNotifications = $derived(
	state.notifications
		.filter((n) => n.status === 'UNREAD')
		.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
		.slice(0, 5)
);
const readNotifications = $derived(state.notifications.filter((n) => n.status === 'READ'));

// Toast-related derived values (consolidated from toastStore.ts)
const activeToasts = $derived(
	state.toasts.filter((toast) => {
		// Auto-expire toasts based on duration
		const now = Date.now();
		const toastAge = now - toast.timestamp.getTime();
		const duration = toast.duration || 5000;
		return toastAge < duration;
	})
);
const hasActiveToasts = $derived(activeToasts.length > 0);

/**
 * Create notification state management
 */
function createNotificationState() {
	// Initialize event handling
	let eventHandlersInitialized = false;

	// Event handler functions declared at scope level for cleanup access
	let handleUnreadCountChanged: (event: any) => void;
	let handleNotificationRead: (event: any) => void;
	let handleNotificationDismissed: (event: any) => void;
	let handlePreferencesUpdated: (event: any) => void;
	let handleCacheCleared: () => void;

	function initializeEventHandlers() {
		if (eventHandlersInitialized) return;
		eventHandlersInitialized = true;
		// Setup event listeners for notification events
		handleUnreadCountChanged = (event: any) => {
			state.unreadCount = event.payload.count;
			logger.debug('Unread count updated via event', { count: event.payload.count });
		};

		handleNotificationRead = (event: any) => {
			const { notification } = event.payload;
			const index = state.notifications.findIndex((n) => n.id === notification.id);
			if (index >= 0) {
				state.notifications[index] = { ...notification };
				logger.debug('Notification marked as read via event', {
					notificationId: notification.id,
					index
				});
			}
		};

		handleNotificationDismissed = (event: any) => {
			const { notification } = event.payload;
			const index = state.notifications.findIndex((n) => n.id === notification.id);
			if (index >= 0) {
				state.notifications[index] = { ...notification };
				logger.debug('Notification dismissed via event', {
					notificationId: notification.id,
					index
				});
			}
		};

		handlePreferencesUpdated = (event: any) => {
			// Reload preferences when updated
			loadPreferences();
			logger.debug('Preferences updated via event, reloading');
		};

		handleCacheCleared = () => {
			state.notifications = [];
			state.unreadCount = 0;
			state.preferences = [];
			state.lastUpdated = null;
			logger.debug('Cache cleared via event');
		};

		// Register event listeners
		eventBus.on('notifications:unread_count_changed', handleUnreadCountChanged);
		eventBus.on('notifications:notification_read', handleNotificationRead);
		eventBus.on('notifications:notification_dismissed', handleNotificationDismissed);
		eventBus.on('notifications:preferences_updated', handlePreferencesUpdated);
		eventBus.on('notifications:cache_cleared', handleCacheCleared);
	}

	// Cleanup function
	function cleanup() {
		if (!eventHandlersInitialized) return;
		eventBus.off('notifications:unread_count_changed', handleUnreadCountChanged);
		eventBus.off('notifications:notification_read', handleNotificationRead);
		eventBus.off('notifications:notification_dismissed', handleNotificationDismissed);
		eventBus.off('notifications:preferences_updated', handlePreferencesUpdated);
		eventBus.off('notifications:cache_cleared', handleCacheCleared);
		eventHandlersInitialized = false;
	}

	/**
	 * Load notifications from the service
	 */
	async function loadNotifications(options?: {
		limit?: number;
		offset?: number;
		status?: string;
		refresh?: boolean;
	}): Promise<void> {
		if (state.isLoading && !options?.refresh) return;

		try {
			state.isLoading = true;
			state.error = null;

			const request = {
				limit: options?.limit || 50,
				offset: options?.offset || 0,
				status: options?.status
			};

			const notifications = await fetchNotifications(request);

			if (options?.offset === 0 || !options?.offset) {
				// Replace all notifications
				state.notifications = notifications;
			} else {
				// Append to existing notifications (pagination)
				state.notifications.push(...notifications);
			}

			state.lastUpdated = new Date();
			state.initialized = true;

			logger.debug('Notifications loaded', {
				count: notifications.length,
				offset: options?.offset || 0
			});

			logger.info('Notifications loaded successfully', {
				count: notifications.length,
				offset: options?.offset || 0,
				status: options?.status || 'all',
				isPagination: !!(options?.offset && options.offset > 0)
			});
		} catch (error) {
			logger.error('Failed to load notifications', {
				error: error instanceof Error ? error.message : String(error),
				options: {
					limit: options?.limit,
					offset: options?.offset,
					status: options?.status
				}
			});
			state.error = error instanceof Error ? error.message : 'Failed to load notifications';
		} finally {
			state.isLoading = false;
		}
	}

	/**
	 * Load user preferences
	 */
	async function loadPreferences(): Promise<void> {
		try {
			const preferences = await fetchPreferences();
			state.preferences = preferences;
			logger.info('Preferences loaded successfully', { count: preferences.length });
		} catch (error) {
			logger.error('Failed to load preferences', {
				error: error instanceof Error ? error.message : String(error)
			});
		}
	}

	/**
	 * Mark notification as read
	 */
	async function markAsRead(notificationId: string): Promise<boolean> {
		try {
			const success = await markAsReadApi(notificationId);
			if (success) {
				// Update local state immediately (don't wait for event bus which may not arrive)
				const index = state.notifications.findIndex((n) => n.id === notificationId);
				if (index >= 0) {
					state.notifications[index] = {
						...state.notifications[index],
						status: 'READ',
						read_at: state.notifications[index].read_at || new Date().toISOString()
					};
					// Decrement unread count if it was unread
					if (state.unreadCount > 0) {
						state.unreadCount--;
					}
				}
				logger.debug('Notification marked as read', { notificationId });
			}
			return success;
		} catch (error) {
			logger.error('Failed to mark notification as read', {
				notificationId,
				error: error instanceof Error ? error.message : String(error)
			});
			return false;
		}
	}

	/**
	 * Dismiss notification
	 */
	async function dismissNotification(notificationId: string): Promise<boolean> {
		try {
			const success = await dismissApi(notificationId);
			if (success) {
				// Update local state immediately (don't wait for event bus which may not arrive)
				const index = state.notifications.findIndex((n) => n.id === notificationId);
				if (index >= 0) {
					const notification = state.notifications[index];
					// Remove from list or mark as dismissed
					state.notifications = state.notifications.filter((n) => n.id !== notificationId);
					// Decrement unread count if it was unread
					if (notification.status === 'UNREAD' && state.unreadCount > 0) {
						state.unreadCount--;
					}
				}
				logger.debug('Notification dismissed', { notificationId });
			}
			return success;
		} catch (error) {
			logger.error('Failed to dismiss notification', {
				notificationId,
				error: error instanceof Error ? error.message : String(error)
			});
			return false;
		}
	}

	/**
	 * Mark all notifications as read
	 */
	async function markAllAsRead(): Promise<number> {
		try {
			const markedCount = await markAllAsReadApi();
			if (markedCount > 0) {
				// Update local state immediately (don't wait for event bus which may not arrive)
				const now = new Date().toISOString();
				state.notifications = state.notifications.map((n) => ({
					...n,
					status: 'READ' as const,
					read_at: n.read_at || now
				}));
				state.unreadCount = 0;
				logger.info('All notifications marked as read', { markedCount });
			}
			return markedCount;
		} catch (error) {
			logger.error('Failed to mark all notifications as read', {
				error: error instanceof Error ? error.message : String(error)
			});
			return 0;
		}
	}

	/**
	 * Refresh all notification data
	 */
	async function refresh(): Promise<void> {
		await Promise.all([loadNotifications({ refresh: true }), loadPreferences()]);
	}

	// --- TOAST FUNCTIONALITY (consolidated from toastStore.ts and notification.store.ts) ---
	let toastIdCounter = 0;

	/**
	 * Create a toast notification
	 */
	function createToast(
		type: ToastMessage['type'],
		message: string,
		duration = 5000,
		options: Partial<ToastMessage> = {}
	): string {
		const id = `toast-${toastIdCounter++}`;
		const toast: ToastMessage = {
			id,
			type,
			message,
			duration,
			timestamp: new Date(),
			...options
		};

		state.toasts = [...state.toasts, toast];
		logger.debug('Toast created', { id, type, message, duration });

		// Auto-remove toast after duration
		if (duration > 0) {
			setTimeout(() => {
				removeToast(id);
			}, duration);
		}

		return id;
	}

	/**
	 * Remove a specific toast
	 */
	function removeToast(id: string): void {
		state.toasts = state.toasts.filter((toast) => toast.id !== id);
		logger.debug('Toast removed', { id });
	}

	/**
	 * Clear all toasts
	 */
	function clearAllToasts(): void {
		const toastCount = state.toasts.length;
		state.toasts = [];
		logger.debug('All toasts cleared', { count: toastCount });
	}

	/**
	 * Toast convenience methods (matching toastStore.ts API)
	 */
	const toastActions = {
		success: (message: string, duration?: number, options?: Partial<ToastMessage>) =>
			createToast('success', message, duration, options),
		error: (message: string, duration?: number, options?: Partial<ToastMessage>) =>
			createToast('error', message, duration, options),
		warning: (message: string, duration?: number, options?: Partial<ToastMessage>) =>
			createToast('warning', message, duration, options),
		info: (message: string, duration?: number, options?: Partial<ToastMessage>) =>
			createToast('info', message, duration, options),
		remove: removeToast,
		clearAll: clearAllToasts
	};

	/**
	 * Show toast function (matching existing API)
	 */
	function showToast(
		message: string,
		type: ToastMessage['type'] = 'info',
		duration = 5000,
		options?: Partial<ToastMessage>
	): string {
		return toastActions[type](message, duration, options);
	}

	/**
	 * Initialize the notification state
	 */
	async function initialize(): Promise<void> {
		if (state.initialized) return;

		logger.info('Initializing notification state');

		try {
			// Initialize event handlers
			initializeEventHandlers();

			// Load initial data in parallel
			await Promise.all([loadNotifications(), loadPreferences()]);

			// Update unread count from API
			state.unreadCount = await fetchUnreadCount();

			state.initialized = true;
			logger.info('Notification state initialized successfully', {
				notificationCount: state.notifications.length,
				unreadCount: state.unreadCount,
				preferenceCount: state.preferences.length
			});
		} catch (error) {
			logger.error('Failed to initialize notification state', {
				error: error instanceof Error ? error.message : String(error)
			});
			state.error = 'Failed to initialize notifications';
		}
	}

	/**
	 * Add notification - alias for showToast for backward compatibility
	 * Maps notificationStore.addNotification() to notificationState.addNotification()
	 */
	function addNotification(notification: Omit<ToastMessage, 'id' | 'timestamp'>): string {
		return showToast(notification.message, notification.type, notification.duration);
	}

	// Return public interface with getter functions for reactive state access
	return {
		// State getters (provide reactive access to $state)
		get notifications() {
			return state.notifications;
		},
		get toasts() {
			return state.toasts;
		},
		get unreadCount() {
			return state.unreadCount;
		},
		get preferences() {
			return state.preferences;
		},
		get isLoading() {
			return state.isLoading;
		},
		get error() {
			return state.error;
		},
		get lastUpdated() {
			return state.lastUpdated;
		},
		get initialized() {
			return state.initialized;
		},

		// Derived values (already using $derived rune)
		get hasUnread() {
			return hasUnread;
		},
		get recentNotifications() {
			return recentNotifications;
		},
		get readNotifications() {
			return readNotifications;
		},
		get activeToasts() {
			return activeToasts;
		},
		get hasActiveToasts() {
			return hasActiveToasts;
		},

		// Notification actions
		loadNotifications,
		loadPreferences,
		markAsRead,
		dismissNotification,
		markAllAsRead,
		refresh,
		initialize,
		cleanup,

		// Toast actions (consolidated from toastStore.ts and notification.store.ts)
		toast: toastActions,
		showToast,
		addNotification, // Add as public method for backward compatibility
		removeToast,
		clearAllToasts
	};
}

// Create and export the notification state
export const notificationState = createNotificationState();

// --- BACKWARD COMPATIBILITY EXPORTS ---
// Export toast functionality for components that used toastStore.ts

export const toasts = {
	subscribe: () => {
		// Return a subscription-like interface for compatibility
		return notificationState.activeToasts;
	},
	success: (message: string, duration?: number, options?: Partial<ToastMessage>) =>
		notificationState.toast.success(message, duration, options),
	error: (message: string, duration?: number, options?: Partial<ToastMessage>) =>
		notificationState.toast.error(message, duration, options),
	warning: (message: string, duration?: number, options?: Partial<ToastMessage>) =>
		notificationState.toast.warning(message, duration, options),
	info: (message: string, duration?: number, options?: Partial<ToastMessage>) =>
		notificationState.toast.info(message, duration, options),
	remove: notificationState.removeToast
};

export const showToast = notificationState.showToast;

// Export notificationStore for compatibility with notification.store.ts
export const notificationStore = {
	subscribe: () => {
		return notificationState.activeToasts;
	},
	addNotification: (notification: Omit<ToastMessage, 'id' | 'timestamp'>) => {
		return notificationState.showToast(
			notification.message,
			notification.type,
			notification.duration
		);
	},
	removeNotification: notificationState.removeToast,
	clearAll: notificationState.clearAllToasts
};
